export default function useUtils() {
  function isAppRunningAsPWA(): Boolean {
    if (!import.meta.client) return false;

    return window.matchMedia("(display-mode: standalone)").matches;
  }

  function pushNotificationConditionsMet(): Boolean {
    if (!import.meta.client) return false;

    return window.Notification != undefined && window.Notification != null;
  }

  async function requestPushNotificationPermissions(): Promise<Boolean> {
    useLogger().log("Requesting notification permissions");

    if (window.Notification == undefined || window.Notification == null) {
      useLogger().log("window.Notification is null or undefined.");
      return false;
    }

    if (window.Notification.permission === "granted") {
      useLogger().log("Permissions already granted.");
      return true;
    } else {
      const value = await window.Notification.requestPermission();

      if (value === "granted") {
        useLogger().log("Permissions granted.");
        return true;
      }
      useLogger().log("Permissions denied.");
      return false;
    }
  }

  function setPWAThemeColor() {
    const isDarkMode = useColorMode().preference == "dark";

    // Change PWA background color because for some reason it is not possible
    // to define a light and dark hex color separately in the config.
    const hexColor = isDarkMode ? "#0e0e0e" : "#f3f4f7";
    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute("content", hexColor);
  }

  function scrollToY(y: number, smooth: boolean = true) {
    const windowScroll = useWindowScroll();

    // Dynamically add and remove the 'scroll-smooth' class, otherwise the smooth
    // scroll happens when user navigates back from fighter details page
    if (smooth) {
      document.getElementsByTagName("html")[0].classList.add("scroll-smooth");
    }

    setTimeout(() => {
      windowScroll.y.value = y;
    }, 50);

    setTimeout(() => {
      document
        .getElementsByTagName("html")[0]
        .classList.remove("scroll-smooth");
    }, 300);
  }

  function scrollToElement(element: HTMLElement | null) {
    if (!element) {
      return;
    }

    // Dynamically add and remove the 'scroll-smooth' class, otherwise the smooth
    // scroll happens when user navigates back from fighter details page

    document.getElementsByTagName("html")[0].classList.add("scroll-smooth");

    setTimeout(() => {
      element?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 50);

    setTimeout(() => {
      element?.focus();
      document
        .getElementsByTagName("html")[0]
        .classList.remove("scroll-smooth");
    }, 300);
  }

  function scrollToFirstError(errors: Record<string, any>) {
    if (errors.length === 0) return;
    const element = document.getElementById(errors[0].id);
    scrollToElement(element);
  }

  function getFullUrl(url: string) {
    if (!url) return url;

    // If the URL already starts with 'http://' or 'https://', return it as is
    if (url.startsWith("http://") || url.startsWith("https://")) {
      return url;
    }

    // Otherwise, prepend 'https://' to the URL
    return `https://${url}`;
  }

  function getUrlWithoutHttp(url: string | undefined | null) {
    if (!url) return url;

    // If the URL starts with 'http://' or 'https://', remove it
    if (url.startsWith("http://")) {
      return url.replace("http://", "");
    } else if (url.startsWith("https://")) {
      return url.replace("https://", "");
    }

    // If the URL does not start with 'http://' or 'https://', return it as is
    return url;
  }

  return {
    isAppRunningAsPWA,
    pushNotificationConditionsMet,
    requestPushNotificationPermissions,
    setPWAThemeColor,
    scrollToY,
    scrollToElement,
    scrollToFirstError,
    getFullUrl,
    getUrlWithoutHttp,
  };
}
